/**
 * feat-305 — the shared assistant panel (ADR 0001 §10.6; design NOTES §2–§6).
 *
 * Presentational over `useAssistantConversation` (the launcher owns the hook so
 * the conversation survives close/reopen). Two presentations, swapped live:
 *  - `docked` (≥ lg): a non-modal `<aside>` under the sticky nav; the page
 *    reflows beside it. No focus trap, no scroll lock.
 *  - `sheet` (< lg): the feat-298 bottom sheet, `role="dialog" aria-modal`,
 *    backdrop, scroll lock, focus trap.
 *
 * PRIVACY: nothing in here is an `<a>` or has a link role; footnotes are plain
 * text; nothing navigates to a transcript or shift view.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  CircleAlert,
  CircleOff,
  Clock,
  CloudOff,
  Copy,
  ListFilter,
  Lock,
  MessageCircleQuestion,
  MessagesSquare,
  RotateCw,
  Sparkles,
  Square,
  SquarePen,
  TimerOff,
  X,
} from 'lucide-react';
import type { AssistantMessage } from './types';
import type { UseAssistantConversation } from './useAssistantConversation';
import type { AssistantPageCopy } from './pages';
import { AssistantMarkdown, FootnoteList, finalizeStoppedBody, markdownToPlainText } from './assistantMarkdown';
import {
  ASSISTANT_COPY,
  ErrorIconName,
  ErrorPresentation,
  RESTORE_ERROR,
  errorPresentation,
} from './copy';
import { conversationToPlainText, isCopyableAnswer } from './conversationText';

export const ASSISTANT_PANEL_ID = 'assistant-panel';
const STICK_PX = 48;
const LIVE_DEBOUNCE_MS = 500;
const COPY_FEEDBACK_MS = 2000;
const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40';
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ERROR_ICONS: Record<ErrorIconName, React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>> = {
  'circle-alert': CircleAlert,
  'timer-off': TimerOff,
  clock: Clock,
  'list-filter': ListFilter,
  'messages-square': MessagesSquare,
  lock: Lock,
  'circle-off': CircleOff,
  'cloud-off': CloudOff,
};

const DIDNT_FINISH: ErrorPresentation = {
  tone: 'neutral',
  icon: 'circle-alert',
  title: ASSISTANT_COPY.didNotFinish,
  body: '',
  action: 'retry',
};

// ── small pieces ─────────────────────────────────────────────────────────────

const RetryButton: React.FC<{ tone: 'danger' | 'neutral'; onClick: () => void; disabled?: boolean }> = ({
  tone,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    data-testid="assistant-retry"
    onClick={onClick}
    disabled={disabled}
    className={`mt-2.5 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
      tone === 'danger'
        ? 'ring-1 ring-danger-line/40 text-danger-text hover:bg-danger-em'
        : 'ring-1 ring-black/15 text-ink hover:bg-cream'
    } ${FOCUS_RING}`}
  >
    <RotateCw className="w-3.5 h-3.5" aria-hidden />
    {ASSISTANT_COPY.tryAgain}
  </button>
);

const NewConversationButton: React.FC<{ onClick: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`mt-2.5 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink/90 disabled:opacity-50 ${FOCUS_RING} focus-visible:ring-offset-2`}
  >
    <SquarePen className="w-3.5 h-3.5" aria-hidden />
    {ASSISTANT_COPY.newConversation}
  </button>
);

export const ErrorBox: React.FC<{
  p: ErrorPresentation;
  onRetry?: () => void;
  onNew?: () => void;
  actionsDisabled?: boolean;
  /** Restore failure shows both actions. */
  both?: boolean;
  as?: 'li' | 'div';
}> = ({ p, onRetry, onNew, actionsDisabled, both, as = 'li' }) => {
  const Icon = ERROR_ICONS[p.icon];
  const Tag = as;
  const danger = p.tone === 'danger';
  const retry = onRetry && (p.action === 'retry' || both) ? (
    <RetryButton tone={danger ? 'danger' : 'neutral'} onClick={onRetry} disabled={actionsDisabled} />
  ) : null;
  const fresh = onNew && (p.action === 'new' || both) ? (
    <NewConversationButton onClick={onNew} disabled={actionsDisabled} />
  ) : null;
  return (
    <Tag
      role="alert"
      data-testid="assistant-error"
      className={`rounded-2xl px-3.5 py-3 text-sm ${danger ? 'bg-danger-surface text-danger-text' : 'bg-cream-dark text-ink'}`}
    >
      <div className="flex gap-2.5">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${danger ? 'text-danger' : 'text-inkMuted'}`} aria-hidden />
        <div className="min-w-0">
          <p className="font-semibold">{p.title}</p>
          {p.body && <p className={`mt-0.5 ${danger ? '' : 'text-inkMuted'}`}>{p.body}</p>}
          {retry && fresh ? (
            <div className="flex flex-wrap gap-2">
              {retry}
              {fresh}
            </div>
          ) : (
            retry || fresh
          )}
        </div>
      </div>
    </Tag>
  );
};

type CopyFeedback = 'idle' | 'copied' | 'failed';

/**
 * "Copy conversation" (Addendum 2 §5): a small secondary action at the end of
 * the conversation. The label swaps to "Copied" / "Couldn't copy" briefly.
 */
const CopyConversationButton: React.FC<{ feedback: CopyFeedback; disabled: boolean; onClick: () => void }> = ({
  feedback,
  disabled,
  onClick,
}) => {
  const Icon = feedback === 'copied' ? Check : feedback === 'failed' ? CircleAlert : Copy;
  const label =
    feedback === 'copied'
      ? ASSISTANT_COPY.copied
      : feedback === 'failed'
        ? ASSISTANT_COPY.copyFailed
        : ASSISTANT_COPY.copyConversation;
  return (
    <div className="mt-5 flex">
      <button
        type="button"
        data-testid="assistant-copy"
        data-feedback={feedback}
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white ring-1 ring-black/15 text-xs font-semibold text-ink hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white ${FOCUS_RING}`}
      >
        <Icon className="w-3.5 h-3.5" aria-hidden />
        {label}
      </button>
    </div>
  );
};

const StatusLine: React.FC<{ label: string }> = ({ label }) => (
  <li className="flex items-center gap-2.5 text-sm text-inkMuted" aria-hidden="true" data-testid="assistant-status">
    <span className="flex gap-1">
      {[0, 150, 300].map((d) => (
        <span
          key={d}
          className="w-1.5 h-1.5 rounded-full bg-inkMuted/70 animate-bounce motion-reduce:animate-none"
          style={{ animationDelay: `${d}ms` }}
        />
      ))}
    </span>
    <span>{label}</span>
  </li>
);

const Caret = () => (
  <span
    className="inline-block w-[2px] h-[1.05em] -mb-[3px] ml-0.5 bg-ink/60 animate-pulse motion-reduce:animate-none"
    aria-hidden="true"
    data-testid="assistant-caret"
  />
);

const StoppedTag = () => (
  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-inkMuted" data-testid="assistant-stopped">
    <Square className="w-3 h-3 fill-current" aria-hidden />
    {ASSISTANT_COPY.stopped}
  </p>
);

const Skeleton = () => (
  <>
    <div className="space-y-5 animate-pulse motion-reduce:animate-none" aria-hidden="true">
      <div className="flex justify-end">
        <div className="h-9 w-3/5 rounded-2xl rounded-br-md bg-cream-dark" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-11/12 rounded bg-cream-dark" />
        <div className="h-3 w-10/12 rounded bg-cream-dark" />
        <div className="h-3 w-7/12 rounded bg-cream-dark" />
      </div>
      <div className="flex justify-end">
        <div className="h-9 w-2/5 rounded-2xl rounded-br-md bg-cream-dark" />
      </div>
    </div>
    <p className="sr-only" role="status">
      {ASSISTANT_COPY.loadingConversation}
    </p>
  </>
);

const formatDividerDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ── panel ────────────────────────────────────────────────────────────────────

export interface AssistantPanelProps {
  convo: UseAssistantConversation;
  copy: AssistantPageCopy;
  presentation: 'docked' | 'sheet';
  onClose: () => void;
  /** Height of the sticky app nav (docked panel sits under it). */
  navHeight: number;
}

export const AssistantPanel: React.FC<AssistantPanelProps> = ({ convo, copy, presentation, onClose, navHeight }) => {
  const { state, send, stop, retry, newConversation, reloadRestore, canSend } = convo;
  const titleId = useId();
  const noteId = useId();
  const inputId = useId();
  const panelRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);
  const rafRef = useRef<number | null>(null);
  const [draft, setDraft] = useState('');
  const [showJump, setShowJump] = useState(false);
  const [live, setLive] = useState('');
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback>('idle');
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const streaming = state.status === 'streaming';
  const loading = state.status === 'loading';
  const sheet = presentation === 'sheet';
  const messages = state.messages;
  const last = messages[messages.length - 1];

  // ── focus on open: textarea, or the heading while restoring ──
  useEffect(() => {
    if (loading) headingRef.current?.focus();
    else textareaRef.current?.focus();
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // When the restore resolves, move focus to the textarea only if it's still on the heading.
  useEffect(() => {
    if (!loading && document.activeElement === headingRef.current) textareaRef.current?.focus();
  }, [loading]);

  // ── sheet: scroll lock (released on close/unmount, i.e. before any navigation) ──
  useLayoutEffect(() => {
    if (!sheet) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheet]);

  // ── auto-scroll: follow the bottom only while the reader is near it ──
  const scrollToBottom = useCallback(() => {
    if (rafRef.current != null) return;
    const doIt = () => {
      rafRef.current = null;
      const el = scrollerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    if (typeof window.requestAnimationFrame === 'function') {
      rafRef.current = window.requestAnimationFrame(doIt);
    } else {
      doIt();
    }
  }, []);
  useEffect(
    () => () => {
      if (rafRef.current != null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafRef.current);
      }
    },
    [],
  );

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_PX;
    stickRef.current = near;
    setShowJump(!near);
  };

  useEffect(() => {
    if (stickRef.current) scrollToBottom();
  }, [messages, state.workingLabel, scrollToBottom]);

  // On restore, start at the bottom.
  useEffect(() => {
    if (!loading) {
      stickRef.current = true;
      setShowJump(false);
      scrollToBottom();
    }
  }, [loading, scrollToBottom]);

  // ── live region (NOTES §6): status (debounced), answer ready, stopped, new ──
  const workingLabel = state.workingLabel;
  const lastBodyEmpty = !!last && last.role === 'assistant' && last.body === '';
  useEffect(() => {
    if (!streaming) return undefined;
    const label = workingLabel || (lastBodyEmpty ? ASSISTANT_COPY.thinking : null);
    if (!label) return undefined;
    const t = setTimeout(() => setLive(label), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [streaming, workingLabel, lastBodyEmpty]);

  const prevSeq = useRef(state.turnSeq);
  useEffect(() => {
    if (state.turnSeq === prevSeq.current) return;
    prevSeq.current = state.turnSeq;
    const m = state.messages[state.messages.length - 1];
    if (state.lastOutcome === 'done' && m) {
      setLive(`${ASSISTANT_COPY.answerReady} ${markdownToPlainText(m.body, m.footnotes)}`);
    } else if (state.lastOutcome === 'stopped') {
      setLive(ASSISTANT_COPY.answerStopped);
    }
  }, [state.turnSeq, state.lastOutcome, state.messages]);

  // ── actions ──
  const doSend = (text: string) => {
    if (!canSend || !text.trim()) return;
    send(text);
    setDraft('');
    stickRef.current = true;
    setShowJump(false);
    scrollToBottom();
  };

  const doStop = () => {
    stop();
    textareaRef.current?.focus();
  };

  useEffect(
    () => () => {
      if (copyTimerRef.current != null) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const showCopyFeedback = (fb: Exclude<CopyFeedback, 'idle'>) => {
    setCopyFeedback(fb);
    setLive(fb === 'copied' ? ASSISTANT_COPY.copied : ASSISTANT_COPY.copyFailed);
    if (copyTimerRef.current != null) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      copyTimerRef.current = null;
      setCopyFeedback('idle');
    }, COPY_FEEDBACK_MS);
  };

  const doCopy = () => {
    if (streaming) return;
    const text = conversationToPlainText(messages);
    const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clip || typeof clip.writeText !== 'function') {
      showCopyFeedback('failed');
      return;
    }
    let p: Promise<void>;
    try {
      p = clip.writeText(text);
    } catch {
      showCopyFeedback('failed');
      return;
    }
    Promise.resolve(p).then(
      () => showCopyFeedback('copied'),
      () => showCopyFeedback('failed'),
    );
  };

  const doNew = () => {
    newConversation();
    setLive(ASSISTANT_COPY.newConversationStarted);
    textareaRef.current?.focus();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (streaming) return;
    doSend(draft);
  };

  const onTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    if (e.shiftKey && !(e.metaKey || e.ctrlKey)) return; // newline
    e.preventDefault();
    if (streaming) return; // draft the next question; Enter doesn't send
    doSend(draft);
  };

  // Autosize the textarea up to max-h-32, then it scrolls.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !e.nativeEvent.isComposing) {
      e.stopPropagation();
      onClose();
      return;
    }
    if (!sheet || e.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const lastItem = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      lastItem.focus();
    } else if (!e.shiftKey && document.activeElement === lastItem) {
      e.preventDefault();
      first.focus();
    }
  };

  // ── message rendering ──
  const disabledByTenant = state.error?.code === 'not_found';
  const renderAssistant = (m: AssistantMessage, isLast: boolean) => {
    const out: React.ReactNode[] = [];
    const isStreaming = m.status === 'streaming' && isLast && streaming;
    if (isStreaming && m.body === '') {
      out.push(<StatusLine key={`${m.id}-status`} label={state.workingLabel || ASSISTANT_COPY.thinking} />);
      return out;
    }
    const failed = m.status === 'error' || m.status === 'blocked';
    if (m.body) {
      out.push(
        <li
          key={m.id}
          data-testid={`assistant-message-${m.id}`}
          data-status={m.status}
          className="text-sm text-ink leading-relaxed [overflow-wrap:anywhere]"
        >
          <span className="sr-only">{ASSISTANT_COPY.assistantPrefix}</span>
          <AssistantMarkdown
            body={m.status === 'stopped' || failed ? finalizeStoppedBody(m.body) : m.body}
            footnotes={m.footnotes}
            trailing={isStreaming ? <Caret /> : null}
          />
          {m.status === 'stopped' && (
            <div>
              <StoppedTag />
            </div>
          )}
          {(m.status === 'complete' || m.status === 'stopped' || failed) && <FootnoteList footnotes={m.footnotes} />}
        </li>,
      );
    }
    if (isStreaming && state.workingLabel) {
      out.push(<StatusLine key={`${m.id}-status`} label={state.workingLabel} />);
    }
    if (failed) {
      const liveError = isLast && state.error;
      let p: ErrorPresentation;
      if (liveError && state.error) p = errorPresentation(state.error.code);
      else if (m.status === 'blocked') p = errorPresentation('privacy_blocked');
      else p = DIDNT_FINISH;
      out.push(
        <ErrorBox
          key={`${m.id}-error`}
          p={p}
          onRetry={isLast ? retry : undefined}
          onNew={isLast ? doNew : undefined}
          actionsDisabled={streaming}
        />,
      );
    }
    return out;
  };

  const showCopy = messages.some(isCopyableAnswer);
  const showEmpty = !loading && state.status !== 'error' && messages.length === 0;
  const subjectName = copy.subjectName;
  const placeholder = subjectName ? `Ask about ${subjectName}…` : ASSISTANT_COPY.placeholderFallback;
  const inputLabel = copy.subjectFullName ? `Ask about ${copy.subjectFullName}` : ASSISTANT_COPY.placeholderFallback;
  const newDisabled = streaming || loading || messages.length === 0;
  const composerDisabled = disabledByTenant || state.restoreError?.code === 'not_found';
  const sendDisabled = !draft.trim() || !canSend;

  const body = loading ? (
    <Skeleton />
  ) : state.status === 'error' ? (
    <ol className="space-y-5" aria-label={ASSISTANT_COPY.conversationLabel}>
      <ErrorBox
        p={state.restoreError?.code === 'not_found' ? errorPresentation('not_found') : RESTORE_ERROR}
        onRetry={state.restoreError?.code === 'not_found' ? undefined : reloadRestore}
        onNew={state.restoreError?.code === 'not_found' ? undefined : doNew}
        both={state.restoreError?.code !== 'not_found'}
      />
    </ol>
  ) : showEmpty ? (
    <div className="pt-1">
      <div className="w-11 h-11 rounded-full bg-coral-light grid place-items-center">
        <Sparkles className="w-5 h-5 text-coral" aria-hidden />
      </div>
      <h3 className="mt-3 font-display text-lg font-semibold text-ink [overflow-wrap:anywhere]">{copy.emptyHeading}</h3>
      <p className="mt-1 text-sm text-inkMuted">{copy.emptyIntro}</p>
      {copy.starters.length > 0 && (
        <>
          <p className="mt-5 text-xs font-semibold text-inkMuted" id={`${titleId}-starters`}>
            {ASSISTANT_COPY.tryAsking}
          </p>
          <ul className="mt-2 space-y-2" aria-labelledby={`${titleId}-starters`}>
            {copy.starters.map((q) => (
              <li key={q}>
                <button
                  type="button"
                  disabled={!canSend}
                  onClick={() => doSend(q)}
                  className={`w-full min-h-[40px] text-left rounded-2xl bg-white ring-1 ring-black/10 px-3.5 py-2.5 text-sm text-ink hover:bg-cream-dark disabled:opacity-60 flex items-start gap-2 ${FOCUS_RING}`}
                >
                  <MessageCircleQuestion className="w-4 h-4 mt-0.5 shrink-0 text-inkMuted" aria-hidden />
                  <span>{q}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  ) : (
    <ol className="space-y-5" aria-label={ASSISTANT_COPY.conversationLabel}>
      {state.restoredAt && (
        <li className="flex items-center gap-3 text-xs text-inkMuted" role="presentation">
          <span className="h-px flex-1 bg-softBorder" />
          <span className="shrink-0">{ASSISTANT_COPY.restoredDivider(formatDividerDate(state.restoredAt))}</span>
          <span className="h-px flex-1 bg-softBorder" />
        </li>
      )}
      {messages.map((m, i) =>
        m.role === 'user' ? (
          <li key={m.id} className="flex justify-end" data-testid={`assistant-message-${m.id}`}>
            <p className="max-w-[85%] rounded-2xl rounded-br-md bg-cream-dark px-3.5 py-2.5 text-sm text-ink whitespace-pre-wrap [overflow-wrap:anywhere]">
              <span className="sr-only">{ASSISTANT_COPY.youAsked}</span>
              {m.body}
            </p>
          </li>
        ) : (
          <React.Fragment key={m.id}>{renderAssistant(m, i === messages.length - 1)}</React.Fragment>
        ),
      )}
    </ol>
  );
  const conversation = (
    <>
      {body}
      {!loading && state.status !== 'error' && showCopy && (
        <CopyConversationButton feedback={copyFeedback} disabled={streaming} onClick={doCopy} />
      )}
    </>
  );

  const frameClass = sheet
    ? 'fixed z-[60] inset-x-0 bottom-0 h-[85dvh] rounded-t-3xl shadow-2xl'
    : 'fixed z-40 right-0 bottom-0 w-[380px] xl:w-[420px] shadow-xl border-l border-softBorder';
  const frameStyle: React.CSSProperties | undefined = sheet
    ? undefined
    : ({ top: `${navHeight}px`, ['--nav-h' as string]: `${navHeight}px` } as React.CSSProperties);

  const content = (
    <>
      {sheet && (
        <div className="pt-2 pb-1 grid place-items-center" aria-hidden="true">
          <span className="w-10 h-1 rounded-full bg-black/15" />
        </div>
      )}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-softBorder">
        <span className="w-9 h-9 shrink-0 rounded-full bg-coral-light grid place-items-center">
          <Sparkles className="w-[18px] h-[18px] text-coral" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            className="font-display font-semibold text-base text-ink truncate focus:outline-none"
          >
            {copy.title}
          </h2>
          {copy.subtitle && (
            <p className="text-xs text-inkMuted truncate" title={copy.subtitle}>
              {copy.subtitle}
            </p>
          )}
        </div>
        <button
          type="button"
          data-testid="assistant-new"
          disabled={newDisabled}
          onClick={doNew}
          aria-label={ASSISTANT_COPY.newConversation}
          title={ASSISTANT_COPY.newConversation}
          className={`h-9 shrink-0 px-2.5 rounded-full inline-flex items-center gap-1.5 text-sm font-semibold text-ink hover:bg-cream-dark disabled:text-inkMuted/60 disabled:hover:bg-transparent disabled:cursor-not-allowed ${FOCUS_RING}`}
        >
          <SquarePen className="w-4 h-4" aria-hidden />
          <span>{ASSISTANT_COPY.newShort}</span>
        </button>
        <button
          type="button"
          data-testid="assistant-close"
          onClick={onClose}
          aria-label={ASSISTANT_COPY.close}
          title={ASSISTANT_COPY.closeTitle}
          className={`w-9 h-9 shrink-0 rounded-full grid place-items-center text-ink hover:bg-cream-dark ${FOCUS_RING}`}
        >
          <X className="w-5 h-5" aria-hidden />
        </button>
      </header>

      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          data-testid="assistant-scroller"
          className="h-full overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4"
        >
          {conversation}
        </div>
        {showJump && !loading && messages.length > 0 && (
          <button
            type="button"
            data-testid="assistant-jump"
            onClick={() => {
              stickRef.current = true;
              setShowJump(false);
              scrollToBottom();
            }}
            className={`absolute left-1/2 -translate-x-1/2 bottom-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white ring-1 ring-black/10 shadow-md text-xs font-semibold text-ink hover:bg-cream ${FOCUS_RING}`}
          >
            <ArrowDown className="w-3.5 h-3.5" aria-hidden />
            {ASSISTANT_COPY.jumpToLatest}
          </button>
        )}
      </div>

      <footer className="border-t border-softBorder px-3 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <form
          onSubmit={onSubmit}
          className="flex items-end gap-2 rounded-2xl bg-white ring-1 ring-black/10 focus-within:ring-2 focus-within:ring-coral/40 pl-3.5 pr-1.5 py-1.5"
        >
          <label htmlFor={inputId} className="sr-only">
            {inputLabel}
          </label>
          <textarea
            id={inputId}
            ref={textareaRef}
            data-testid="assistant-input"
            rows={1}
            value={draft}
            disabled={composerDisabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onTextKeyDown}
            aria-describedby={noteId}
            placeholder={placeholder}
            className="flex-1 min-w-0 resize-none bg-transparent py-1.5 text-sm text-ink placeholder:text-inkMuted focus:outline-none max-h-32 leading-5 disabled:cursor-not-allowed"
          />
          {/* One button element for Send and Stop, so focus doesn't jump. */}
          <button
            type={streaming ? 'button' : 'submit'}
            data-testid={streaming ? 'assistant-stop' : 'assistant-send'}
            aria-label={streaming ? ASSISTANT_COPY.stop : ASSISTANT_COPY.send}
            title={streaming ? ASSISTANT_COPY.stopTitle : ASSISTANT_COPY.send}
            disabled={streaming ? false : sendDisabled}
            onClick={
              streaming
                ? (e) => {
                    e.preventDefault();
                    doStop();
                  }
                : undefined
            }
            className={`w-9 h-9 shrink-0 rounded-full grid place-items-center ${
              streaming ? 'bg-ink text-white hover:bg-ink/90' : 'bg-coral text-white hover:bg-coral/90'
            } disabled:bg-black/10 disabled:text-inkMuted disabled:cursor-not-allowed ${FOCUS_RING} focus-visible:ring-offset-2`}
          >
            {streaming ? (
              <Square className="w-3.5 h-3.5 fill-current" aria-hidden />
            ) : (
              <ArrowUp className="w-4 h-4" aria-hidden />
            )}
          </button>
        </form>
        <p id={noteId} className="mt-2 px-1 text-xs leading-snug text-inkMuted">
          {copy.footerNote}
        </p>
      </footer>
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="assistant-live">
        {live}
      </div>
    </>
  );

  if (sheet) {
    return (
      <>
        <div className="fixed inset-0 z-[60] bg-black/40" aria-hidden="true" onClick={onClose} data-testid="assistant-backdrop" />
        <div
          ref={(el) => {
            panelRef.current = el;
          }}
          id={ASSISTANT_PANEL_ID}
          data-testid="assistant-panel"
          data-presentation="sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onKeyDown={onPanelKeyDown}
          className={`${frameClass} bg-white flex flex-col`}
        >
          {content}
        </div>
      </>
    );
  }
  return (
    <aside
      ref={(el) => {
        panelRef.current = el;
      }}
      id={ASSISTANT_PANEL_ID}
      data-testid="assistant-panel"
      data-presentation="docked"
      aria-labelledby={titleId}
      onKeyDown={onPanelKeyDown}
      style={frameStyle}
      className={`${frameClass} bg-white flex flex-col`}
    >
      {content}
    </aside>
  );
};

/**
 * Error boundary around the PANEL ONLY (NOTES §4 page isolation): a render
 * error inside the panel shows the upstream box with Try again (reload panel),
 * and the page is untouched.
 */
export class AssistantPanelBoundary extends React.Component<
  { children: React.ReactNode; onClose: () => void; presentation: 'docked' | 'sheet'; navHeight: number },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    // Never log message content; the boundary only records that it tripped.
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const sheet = this.props.presentation === 'sheet';
    return (
      <div
        id={ASSISTANT_PANEL_ID}
        data-testid="assistant-panel"
        role={sheet ? 'dialog' : undefined}
        aria-modal={sheet ? true : undefined}
        aria-label={ASSISTANT_COPY.panelError}
        style={sheet ? undefined : { top: `${this.props.navHeight}px` }}
        className={`${
          sheet
            ? 'fixed z-[60] inset-x-0 bottom-0 h-[85dvh] rounded-t-3xl shadow-2xl'
            : 'fixed z-40 right-0 bottom-0 w-[380px] xl:w-[420px] shadow-xl border-l border-softBorder'
        } bg-white flex flex-col p-4 gap-3`}
      >
        <div className="flex justify-end">
          <button
            type="button"
            onClick={this.props.onClose}
            aria-label={ASSISTANT_COPY.close}
            title={ASSISTANT_COPY.closeTitle}
            className={`w-9 h-9 rounded-full grid place-items-center text-ink hover:bg-cream-dark ${FOCUS_RING}`}
          >
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>
        <ErrorBox as="div" p={errorPresentation('upstream')} onRetry={() => this.setState({ failed: false })} />
      </div>
    );
  }
}
